/**
 * Inner ingest seam — archive a single file in Supabase Storage and
 * register it in athlete_files.
 *
 * Called by:
 *   - src/db/ingest.js (every parsed-source upload)
 *   - src/ui/components/ta-files-tab.js (the "+ Add file" arbitrary upload)
 *
 * Best-effort with explicit signal (C2 policy):
 *   - On Storage write failure → returns { archived: false, reason } instead
 *     of throwing. The orchestrator decides whether that's a hard error
 *     (arbitrary-file path) or a degraded-but-OK outcome (parsed sources
 *     where the parsed data is the priority).
 *   - On athlete_files insert failure (Storage already succeeded) → returns
 *     { archived: true, registryMissing: true }. The file is in Storage; it
 *     just won't show up in the Files tab. Rare in practice.
 */

import { supabase } from './supabase.js';

const STORAGE_BUCKET = 'athlete-files';

/**
 * Archive an artifact: write to Storage, insert athlete_files row.
 *
 * Returns one of:
 *   { archived: true,  fileId, storagePath, byteSize, mime }
 *   { archived: true,  fileId, storagePath, byteSize, mime, registryMissing: true }
 *   { archived: false, reason, fileId, byteSize, mime }
 */
export async function archiveArtifact({
  blob,
  filename,
  athleteId,
  sessionDate,
  source,
  uploadedAt,
  fileId,
  notes = null,
}) {
  if (!athleteId) throw new Error('archiveArtifact: athleteId is required');
  if (!sessionDate) throw new Error('archiveArtifact: sessionDate is required');
  if (!source) throw new Error('archiveArtifact: source is required');
  if (!blob) throw new Error('archiveArtifact: blob is required');

  const id = fileId ?? generateUuid();
  const safeFilename = sanitizeFilename(filename);
  const byteSize = blob.size ?? 0;
  const mime = blob.type || guessMimeFromExtension(filename) || 'application/octet-stream';
  const intendedStoragePath = `${athleteId}/${id}_${safeFilename}`;
  const uploadedAtIso = uploadedAt ?? new Date().toISOString();

  // Storage write — best-effort.
  let storagePath = null;
  let storageError = null;
  try {
    const { error } = await supabase.storage
      .from(STORAGE_BUCKET)
      .upload(intendedStoragePath, blob, { contentType: mime, upsert: false });
    if (error) throw error;
    storagePath = intendedStoragePath;
  } catch (err) {
    storageError = err;
    console.warn(
      '[archive] Storage write skipped — bucket likely missing or quota exhausted:',
      err.message ?? err
    );
  }

  if (!storagePath) {
    return {
      archived: false,
      reason: storageError?.message ?? 'storage_write_failed',
      fileId: id,
      byteSize,
      mime,
    };
  }

  // athlete_files row — also best-effort (Storage already succeeded).
  try {
    const { error } = await supabase.from('athlete_files').insert({
      id,
      athlete_id: athleteId,
      session_date: sessionDate,
      source,
      original_filename: filename,
      storage_path: storagePath,
      mime,
      byte_size: byteSize,
      uploaded_at: uploadedAtIso,
      notes,
    });
    if (error) throw error;
  } catch (err) {
    console.warn(
      '[archive] athlete_files row skipped — table likely missing:',
      err.message ?? err
    );
    return {
      archived: true,
      fileId: id,
      storagePath,
      byteSize,
      mime,
      registryMissing: true,
    };
  }

  return { archived: true, fileId: id, storagePath, byteSize, mime };
}

/**
 * Cleanup helper used by ingest.js when a downstream insert fails and we
 * need to roll back the Storage object + athlete_files row. Best-effort —
 * cleanup errors are swallowed so they never mask the original failure.
 */
export async function removeArchivedArtifact(storagePath, fileId) {
  if (storagePath) {
    try {
      await supabase.storage.from(STORAGE_BUCKET).remove([storagePath]);
    } catch { /* swallow */ }
  }
  if (fileId) {
    try {
      await supabase.from('athlete_files').delete().eq('id', fileId);
    } catch { /* swallow */ }
  }
}

// ============================================================
// Helpers
// ============================================================

function sanitizeFilename(name) {
  return String(name).replace(/[\/\\\x00-\x1f]/g, '_');
}

function generateUuid() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

function guessMimeFromExtension(name) {
  if (!name) return null;
  const ext = name.toLowerCase().match(/\.([a-z0-9]+)$/)?.[1];
  if (!ext) return null;
  const map = {
    csv: 'text/csv',
    pdf: 'application/pdf',
    png: 'image/png',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    heic: 'image/heic',
    mp4: 'video/mp4',
    mov: 'video/quicktime',
    txt: 'text/plain',
  };
  return map[ext] ?? null;
}
